import React, { useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import BillingPeriodCell from '../BillingPeriodCell';
import { getActiveBillingCycles } from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { daysToPayDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { BillingDataTable } from '../BillingDataTable';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn } from '../billingTableUtils';
import { PatientCell } from '../PatientCell';
import { InsuranceCell } from '../InsuranceCell';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';

interface EOEPaidTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEPaidTab: React.FC<EOEPaidTabProps> = () => {
  const query = useBillingTableQuery('paid', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const { data, isLoading, isRefreshing, total, totalPages } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    tabId: 'paid',
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PAID'], ...query.toParams() },
    errorMessage: 'Could not fetch data for the "Paid" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <PatientCell
            name={row.original.patient_name}
            mrn={row.original.patient_mrn}
          />
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        sortingFn: currencySortingFn,
      },
      {
        id: 'days_to_pay',
        header: 'Days to Pay',
        accessorFn: row => daysToPayDays(row.billed_at, row.paid_date) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            mode='daysToPay'
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
            paidDate={row.original.paid_date}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={<ClaimWorksheetViewerAction billingCycle={row.original} />}
          />
        ),
      },
    ],
    []
  );

  return (
    <div className='flex flex-col gap-4'>
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
      />
    </div>
  );
};

export default EOEPaidTab;
